'use client';

import { useLocale, useTranslations } from '@fshr/context/i18n-context';
import { addYears, endOfDay, format } from 'date-fns';
import type { ParsedAvailabilities, Value } from '@fshr/shared/types';
import Input from '../input';
import Calendar from '../calendar';
import Icon from '../icon';
import ItineraryDefault from '../icon/itinerary-default';
import { toZonedTime } from 'date-fns-tz';
import Dialog from '../dialog';
import { useDatePicker } from './useDatePicker';
import { cn } from '@fshr/shared-react';

interface EditPageProps {
	checkInDate: string | undefined;
	checkOutDate: string | undefined;
	availability: ParsedAvailabilities;
	timezone: string;
	onChange: (startDate: Date, endDate: Date) => void;
	onCloseDialog?: (
		isDialogOpen: boolean,
		closeFiredWith?: { key?: string; event?: unknown }
	) => void | Promise<void>;
	onOpenDialog?: (isDialogOpen: boolean) => void | Promise<void>;
	isMobile?: boolean;
	lastUpdated?: string;
	additionalTileClasses?: string;
}
const DatePicker = ({
	checkInDate,
	checkOutDate,
	availability,
	timezone,
	onChange,
	onCloseDialog,
	onOpenDialog,
	lastUpdated,
	additionalTileClasses = '',
}: EditPageProps) => {
	const locale = useLocale();
	const t = useTranslations();

	const {
		calendarValue,
		endDate,
		isCheckoutDateRequired,
		startDate,
		dropdownPosition,
		stayDate,
		isOpen,
		containerRef,
		inputRef,
		isDesktop,
		handleCloseDialog,
		handleOpenDialog,
		setEndDateValue,
		setIsCheckoutDateRequired,
		setStartDateValue,
		setStayDate,
		setCalendarValue,
	} = useDatePicker({
		checkInDate,
		checkOutDate,
		onCloseDialog,
		onOpenDialog,
	});

	const renderDropdown = () => {
		if (!isOpen) return null;

		return (
			<Dialog
				position={dropdownPosition}
				isOpen={isOpen}
				onClose={handleCloseDialog}
				title={'Title'}
				isPopup={isDesktop}
			>
				<div
					className={cn('w-full header:max-w-[590px]', {
						'spacing-card-padding-xs': !isDesktop,
					})}
				>
					<Calendar
						showDoubleView={isDesktop}
						showNavigation={isDesktop}
						onClose={() => {
							handleCloseDialog();
						}}
						onChange={(value: Value, _) => {
							if (Array.isArray(value)) {
								if (!value?.[1])
									return setStayDate(`${format(value?.[0] as Date, 'MM/dd/yyyy')} - `);

								const startDate = value?.[0];
								const endDate = value?.[1];

								setStartDateValue(startDate as Date);
								setEndDateValue(endDate);

								setStayDate(
									`${format(startDate ?? '', 'MM/dd/yyyy')} - ${format(endDate ?? '', 'MM/dd/yyyy')}`
								);
								if (!startDate || !endDate) return;
								setCalendarValue([startDate, endDate]);
							}
						}}
						nextAriaLabel={t('Next month')}
						prevAriaLabel={t('Previous month')}
						nextLabel={<Icon icon="chevron_right"></Icon>}
						// prevLabel={<Icon icon="chevron_left"></Icon>}
						navigationAriaLabel={''}
						navigationAriaLive="polite"
						selectRange={true}
						strictRangeSelect={true}
						next2Label={null}
						prev2Label={null}
						goToRangeStartOnSelect={false}
						tileClassName={additionalTileClasses}
						tileAriaLabel={d => d.getDate().toString()}
						tileDisabled={undefined}
						locale={locale}
						formatShortWeekday={(_, d) => format(d, locale === 'en' ? 'eeeee' : 'eeeeee')}
						minDate={undefined}
						maxDate={endOfDay(addYears(toZonedTime(new Date(), timezone), 1))}
						allowPartialRange={true}
						timezone={timezone}
						value={calendarValue}
						valueLabel={stayDate}
						restrictedDates={availability.restricted}
						unavailableDates={availability.unavailable}
						onSubmit={() => {
							setIsCheckoutDateRequired(!endDate);
							if (!startDate || !endDate) {
								handleOpenDialog();
							} else {
								handleCloseDialog();
							}
							if (startDate && endDate) return onChange(startDate, endDate);
						}}
						isCheckoutDateRequired={isCheckoutDateRequired}
						isMobile={!isDesktop}
						lastUpdated={lastUpdated}
					/>
				</div>
			</Dialog>
		);
	};

	return (
		<div ref={containerRef} className="flex flex-col w-full relative">
			{/* Fix for Remove Stale issue - improve input styling and ensure it doesn't appear stale */}
			<Input
				icon={<ItineraryDefault />}
				ref={inputRef}
				onClickIcon={() => handleOpenDialog()}
				id="dateOfArrival"
				readOnly={true}
				required={true}
				aria-haspopup="dialog"
				aria-expanded={isOpen}
				wrapperClassName="pb-[0px]"
				className={cn(
					'text-body2 bg-surface-form px-[16px] py-[8.5px] border-b border-border-default',
					'hover:bg-surface-hover focus:bg-surface-form transition-colors duration-200',
					'cursor-pointer'
				)}
				label={t('Check in - Check out')}
				labelStyle="text-label"
				placeholder={t('')}
				value={stayDate}
				onKeyDown={e => {
					if (e.key === 'Enter') {
						e.preventDefault();
						handleOpenDialog();
					}
				}}
				onClick={() => handleOpenDialog()}
			/>
			{renderDropdown()}
		</div>
	);
};

export default DatePicker;
